"use client"
/* eslint-disable react-hooks/immutability -- These refs point to DOM surfaces; positioning intentionally updates DOM styles, never React props/state. */

import { useLayoutEffect, useRef, type RefObject } from "react"

/** Keeps Arc's moving surface in the DOM, while lifting its paint above scroll clipping. */
export function useTopLayer(open: boolean, anchor: RefObject<HTMLElement | null>, surface: RefObject<HTMLElement | null>, clampX = true) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useLayoutEffect(() => {
    const node = surface.current
    clearTimeout(timer.current)
    if (!node || typeof node.showPopover !== "function") return
    const reset = () => {
      if (node.matches(":popover-open")) node.hidePopover()
      node.style.removeProperty("position"); node.style.removeProperty("left"); node.style.removeProperty("top"); node.style.removeProperty("right"); node.style.removeProperty("bottom")
      delete node.dataset.topLayer
    }
    if (!open && !node.matches(":popover-open")) return
    node.dataset.topLayer = ""
    const place = () => {
      const target = anchor.current
      if (!target || !node.isConnected) return
      const box = target.getBoundingClientRect(), margin = 12
      node.style.position = "fixed"; node.style.right = "auto"; node.style.bottom = "auto"
      node.style.left = `${clampX ? Math.max(margin, Math.min(box.left, document.documentElement.clientWidth - node.offsetWidth - margin)) : box.left}px`
      node.style.top = `${Math.max(margin, Math.min(box.top, window.innerHeight - node.offsetHeight - margin))}px`
    }
    place()
    if (open && !node.matches(":popover-open")) node.showPopover()
    const observer = new ResizeObserver(place)
    observer.observe(node); if (anchor.current) observer.observe(anchor.current)
    window.addEventListener("resize", place); document.addEventListener("scroll", place, true)
    const dispose = () => { observer.disconnect(); window.removeEventListener("resize", place); document.removeEventListener("scroll", place, true) }
    if (!open) timer.current = setTimeout(() => { reset(); dispose() }, 500)
    return () => { clearTimeout(timer.current); dispose() }
  }, [open, anchor, surface, clampX])
  useLayoutEffect(() => () => { clearTimeout(timer.current) }, [])
}
